import * as React from 'react';
import { Field, Form, Formik } from 'formik';
import { AppUser } from 'Src/views/App/App';
import * as Yup from 'yup';
import propz from 'propz';
import { useState } from 'react';
import { Switch } from 'Src/components/Switch/Switch';
import { SchoolFile, SchoolFileCategory } from '../../../../../../models/schoolFile';
import { uploadFileAllPath } from '../../../../../../helpers/service/file';
import { FILES_CATEGORY, FILES_CATEGORY_SERVER_TO_CLIENT_MAPPING } from '../../../../../../consts/file';
import { ALLOWED_MIME_TYPES } from '../../../../../../helpers/file/file';

interface Props {
  file: SchoolFile;
  onSubmit: (data) => void;
  onCancel: () => void;
  user: AppUser;
  categories: SchoolFileCategory[];
}

const SchoolLeagueSchema = Yup.object().shape({
  title: Yup.string().required('Required')
});

const getUploadedFileName = (url: string): string => {
  const cleanUrl = String(url || '').split('?')[0];
  const fileName = cleanUrl.split('/').pop() || '';
  try {
    return decodeURIComponent(fileName);
  } catch {
    return fileName;
  }
};

export const SchoolFileForm: React.FunctionComponent<Props> = props => {
  const { file, onSubmit, onCancel, user, categories } = props;
  const isFileExist = typeof file !== 'undefined';

  const [picUrl, setPicUrl] = useState(propz.get(file, ['url'], ''));
  const [isFileOtherError, setIsFileOtherError] = useState(false);
  const [textError, setTextError] = useState('');
  const [selectedFileName, setSelectedFileName] = useState(
    isFileExist ? getUploadedFileName(propz.get(file, ['url'], '')) : ''
  );
  const [isUploading, setIsUploading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  const leagueForm = {
    title: isFileExist ? file.title : '',
    isPublished: isFileExist ? file.isPublished : false,
    category: isFileExist ? file.category : categories.length ? categories[0].key : FILES_CATEGORY.CALENDAR
  };

  const isCurrentCategoryMissing = isFileExist && !categories.some(category => category.key === file.category);

  const onFileSelected = event => {
    const file = event.target.files[0];
    if (!file) return;
    const isAllowedMimeType = ALLOWED_MIME_TYPES.some(type => file.type === type);

    if (isAllowedMimeType) {
      setIsUploading(true);
      setIsFileOtherError(false);
      setTextError('');
      const fileName = file.name;
      const fileNameWithoutSpaces = fileName.split(' ').join('_');
      const formData = new FormData();
      formData.append('file', file, fileNameWithoutSpaces);
      uploadFileAllPath(user, formData)
        .then(response => {
          const { link } = response;
          setPicUrl(link);
          setSelectedFileName(file.name);
          setIsFileOtherError(false);
          setTextError('');
          setIsUploading(false);
        })
        .catch(error => {
          console.error(error);
          setTextError('Error uploading file');
          setIsFileOtherError(true);
          setIsUploading(false);
        });
    } else {
      setTextError('The file format is not valid');
      setIsFileOtherError(true);
    }
  };

  return (
    <div className="schoolFileEditor">
          <Formik
            initialValues={leagueForm}
            validationSchema={SchoolLeagueSchema}
            onSubmit={values => {
              if (typeof picUrl !== 'undefined' && picUrl !== '' && !isFileOtherError && !isUploading) {
                const { title, isPublished, category } = values;
                const dataToSubmit = {
                  title,
                  isPublished,
                  category,
                  url: picUrl
                };
                onSubmit(dataToSubmit);
              } else {
                setTextError('Required');
                setIsFileOtherError(true);
              }
            }}
            >
            {({ touched, errors, setFieldValue, values }) => (
              <Form className="schoolFileEditor__form">
                <label className="schoolFileEditor__field">
                  <span>Title</span>
                  <Field type="text" name="title" placeholder="Document title" />
                  {touched.title && errors.title ? <em>{errors.title}</em> : null}
                </label>

                <div className="schoolFileEditor__publish">
                  <Field
                    name="isPublished"
                  >
                    {({ field }) => (
                      <Switch
                        {...field}
                        value={values.isPublished}
                        customClass=""
                        onChange={event => {
                          const isPublished = event.target.checked;
                          setFieldValue('isPublished', isPublished);
                        }}
                      />
                    )}
                  </Field>
                  <div><strong>Published</strong><span>Make this document available for download.</span></div>
                </div>

                <label className="schoolFileEditor__field">
                  <span>Category</span>
                  <Field component="select" name="category" onChange={event => setFieldValue('category', event.target.value)}>
                    {categories.length ? (
                      <>
                        {isCurrentCategoryMissing ? <option value={file.category}>{file.category}</option> : null}
                        {categories.map(category => (
                          <option key={category.id || category.key} value={category.key}>{category.name}</option>
                        ))}
                      </>
                    ) : (
                      <>
                        <option value={FILES_CATEGORY.CALENDAR}>{FILES_CATEGORY_SERVER_TO_CLIENT_MAPPING.CALENDAR}</option>
                        <option value={FILES_CATEGORY.RULES}>{FILES_CATEGORY_SERVER_TO_CLIENT_MAPPING.RULES}</option>
                        <option value={FILES_CATEGORY.RISK_ASSESSMENT}>{FILES_CATEGORY_SERVER_TO_CLIENT_MAPPING.RISK_ASSESSMENT}</option>
                      </>
                    )}
                  </Field>
                </label>
                <div className="schoolFileEditor__field">
                  <span>File</span>
                  <label className={`schoolFileEditor__upload ${isFileOtherError ? 'isError' : ''} ${isUploading ? 'isUploading' : ''}`}>
                    <b>{isUploading ? 'Uploading file...' : picUrl ? 'Replace file' : 'Choose a file'}</b>
                    <small>{isUploading ? 'Please wait until the upload is complete' : 'PDF, Word, Excel or another supported document'}</small>
                    <input key={fileInputKey} type="file" name="picUrl" disabled={isUploading} onChange={onFileSelected} />
                  </label>
                  {picUrl && !isFileOtherError ? (
                    <div className="schoolFileEditor__uploadedFile">
                      <span>{selectedFileName || 'Uploaded document'}</span>
                      <button type="button" aria-label="Remove file" onClick={() => {
                        setPicUrl('');
                        setSelectedFileName('');
                        setFileInputKey(key => key + 1);
                      }}>×</button>
                    </div>
                  ) : null}
                  {isFileOtherError ? <em>{textError}</em> : null}
                </div>

                <div className="schoolFileEditor__actions">
                  <button type="button" onClick={onCancel}>Cancel</button>
                  <button type="submit" className="isPrimary" disabled={isUploading}>
                    {isFileExist ? 'Save changes' : 'Add document'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
    </div>
  );
};

SchoolFileForm.displayName = 'SchoolFileForm';

